import { Table } from "@tanstack/react-table";
import {
  Card,
  Button,
  ButtonGroup,
  DropdownButton,
  Dropdown,
  ToggleButton,
} from "react-bootstrap";
import {
  BsFilter,
  BsX,
  BsLayoutThreeColumns,
  BsPrinter,
  BsBox,
  BsCalendarDate,
  BsCheckSquare,
  // BsClock,
  BsSignpost2,
  BsTruck,
  BsSearch,
  BsArrowsAngleContract,
  BsArchive,
  BsPersonCheck,
  BsCalendarCheck,
  BsPerson,
} from "react-icons/bs";
import BookingColumns from "../booking-columns/BookingColumns";
import BookingFilters, { FilterCode } from "../booking-filters/BookingFilters";
import { Booking } from "../booking-page/store/slice";
import styles from "./bookingSettings.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo } from "react";
import bookingPageSlice from "../booking-page/store/slice";
import moment from "moment";
import bookingFilterSlice from "../booking-filters/store/slice";
import { Status } from "../statuses/store/slice";
import { CargoType } from "../cargo-types/store/slice";
import bookingSettingSlice, { PerPage, TableSize } from "./store/slice";
import { VehicleType } from "../vehicle-types/store/slice";
import { Route } from "../routes/store/slice";
import { User } from "../users/store/slice";
import { TrailerType } from "../trailer-types/store/slice";
import { FaTrailer } from "react-icons/fa";

type Props = {
  table: Table<Booking>;
};

const BookingSettings = ({ table }: Props) => {
  const dispatch = useDispatch();

  const isFiltersOpened = useSelector(
    (state) => state.bookingPage.isFiltersOpened
  );
  const isColumnsOpened = useSelector(
    (state) => state.bookingPage.isColumnsOpened
  );

  const departureFrom = useSelector(
    (state) => state.bookingFilters.departureFrom
  );
  const departureTo = useSelector((state) => state.bookingFilters.departureTo);
  const arrivalFrom = useSelector((state) => state.bookingFilters.arrivalFrom);
  const arrivalTo = useSelector((state) => state.bookingFilters.arrivalTo);
  // const startTime = useSelector((state) => state.bookingFilters.startTime);
  // const endTime = useSelector((state) => state.bookingFilters.endTime);
  const selectedRoutes = useSelector(
    (state) => state.bookingFilters.selectedRoutes
  );
  const selectedStatuses = useSelector(
    (state) => state.bookingFilters.selectedStatuses
  );
  const selectedVehicleTypes = useSelector(
    (state) => state.bookingFilters.selectedVehicleTypes
  );
  const selectedTrailerTypes = useSelector(
    (state) => state.bookingFilters.selectedTrailerTypes
  );
  const selectedCargoTypes = useSelector(
    (state) => state.bookingFilters.selectedCargoTypes
  );
  const tableSize = useSelector((state) => state.bookingSettings.tableSize);
  const perPage = useSelector((state) => state.bookingSettings.perPage);
  const searchFilter = useSelector(
    (state) => state.bookingFilters.searchFilter
  );
  const selectedCustomers = useSelector(
    (state) => state.bookingFilters.selectedCustomers
  );
  const archived = useSelector((state) => state.bookingFilters.archived);
  const createdByMe = useSelector((state) => state.bookingFilters.createdByMe);

  const setIsFiltersOpened = useCallback(
    (opened: boolean) =>
      dispatch(bookingPageSlice.actions.setIsFiltersOpened(opened)),
    [dispatch]
  );
  const setIsColumnsOpened = useCallback(
    (opened: boolean) =>
      dispatch(bookingPageSlice.actions.setIsColumnsOpened(opened)),
    [dispatch]
  );
  const setOpenedFilters = useCallback(
    (codes: FilterCode[]) =>
      dispatch(bookingFilterSlice.actions.setOpenedFilters(codes)),
    [dispatch]
  );
  const setDepartureFrom = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setDepartureFrom(date)),
    [dispatch]
  );
  const setDepartureTo = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setDepartureTo(date)),
    [dispatch]
  );
  const setArrivalFrom = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setArrivalFrom(date)),
    [dispatch]
  );
  const setArrivalTo = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setArrivalTo(date)),
    [dispatch]
  );
  // const setStartTime = useCallback(
  //   (date: Date | null) =>
  //     dispatch(bookingFilterSlice.actions.setStartTime(date)),
  //   [dispatch]
  // );
  // const setEndTime = useCallback(
  //   (date: Date | null) =>
  //     dispatch(bookingFilterSlice.actions.setEndTime(date)),
  //   [dispatch]
  // );
  const toggleRoute = useCallback(
    (route: Route) => dispatch(bookingFilterSlice.actions.toggleRoute(route)),
    [dispatch]
  );
  const toggleStatus = useCallback(
    (status: Status) =>
      dispatch(bookingFilterSlice.actions.toggleStatus(status)),
    [dispatch]
  );
  const toggleVehicleType = useCallback(
    (type: VehicleType) =>
      dispatch(bookingFilterSlice.actions.toggleVehicleType(type)),
    [dispatch]
  );
  const toggleTrailerType = useCallback(
    (type: TrailerType) =>
      dispatch(bookingFilterSlice.actions.toggleTrailerType(type)),
    [dispatch]
  );
  const toggleCargoType = useCallback(
    (type: CargoType) =>
      dispatch(bookingFilterSlice.actions.toggleCargoType(type)),
    [dispatch]
  );
  const setTableSize = useCallback(
    (size: TableSize) =>
      dispatch(bookingSettingSlice.actions.setTableSize(size)),
    [dispatch]
  );
  const setPerPage = useCallback(
    (size: PerPage) => dispatch(bookingSettingSlice.actions.setPerPage(size)),
    [dispatch]
  );
  const setSearchFilter = useCallback(
    (query: string) =>
      dispatch(bookingFilterSlice.actions.setSearchFilter(query)),
    [dispatch]
  );
  const toggleArchived = useCallback(
    () => dispatch(bookingFilterSlice.actions.toggleArchived()),
    [dispatch]
  );
  const toggleCreatedByMe = useCallback(
    () => dispatch(bookingFilterSlice.actions.toggleCreatedByMe()),
    [dispatch]
  );
  const toggleCustomer = useCallback(
    (customer: User) =>
      dispatch(bookingFilterSlice.actions.toggleCustomer(customer)),
    [dispatch]
  );

  const prepareTable = (callback: (newWindow: Window) => void) => {
    const tableDiv = document.getElementById("bookings-table");
    const newWindow = window.open("/");

    if (tableDiv && newWindow) {
      newWindow.document.write(tableDiv.outerHTML);

      const style = newWindow.document.createElement("style");

      style.appendChild(
        newWindow.document.createTextNode(
          `table { font-size: 12px; table-layout: auto; width: 100% !important; } th, td { text-align: left; }`
        )
      );

      // add styles
      newWindow.document.getElementsByTagName("head")[0].appendChild(style);

      const table = newWindow.document.getElementById(
        "bookings-table"
      ) as HTMLTableElement;

      if (table) {
        const rows = table.rows;

        // remove action column
        for (var i = 0; i < rows.length; i++) {
          rows[i].deleteCell(-1);
        }

        const svgs = table.getElementsByTagName("svg");

        // remove icons
        while (svgs[0]) {
          svgs[0].remove();
        }
      }

      callback(newWindow);

      newWindow.close();
    }
  };

  const filtersCount = useMemo(() => {
    const filters: boolean[] = [
      archived,
      createdByMe,
      !!searchFilter,
      !!departureFrom,
      !!departureTo,
      !!arrivalFrom,
      !!arrivalTo,
      !!selectedRoutes.length,
      !!selectedStatuses.length,
      !!selectedVehicleTypes.length,
      !!selectedTrailerTypes.length,
      !!selectedCargoTypes.length,
      !!selectedCustomers.length,
    ];

    return filters.filter(Boolean).length;
  }, [
    archived,
    createdByMe,
    searchFilter,
    departureFrom,
    departureTo,
    arrivalFrom,
    arrivalTo,
    selectedRoutes,
    selectedStatuses,
    selectedVehicleTypes,
    selectedTrailerTypes,
    selectedCargoTypes,
    selectedCustomers,
  ]);

  return (
    <Card className={styles.card}>
      <Card.Body className={styles.body}>
        <div>
          <div>
            <Button
              className={styles.filterButton}
              onClick={() => setIsFiltersOpened(true)}
            >
              <BsFilter /> Filters{" "}
              {!!filtersCount && (
                <span className={styles.filtersCount}>({filtersCount})</span>
              )}
            </Button>
          </div>

          <div className={styles.filters}>
            {archived && (
              <ButtonGroup size="sm" className={styles.filter}>
                <Button
                  variant="danger"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([]);
                  }}
                >
                  <BsArchive /> Archived
                </Button>
                <Button variant="danger" onClick={toggleArchived}>
                  <BsX />
                </Button>
              </ButtonGroup>
            )}

            {createdByMe && (
              <ButtonGroup size="sm" className={styles.filter}>
                <Button
                  variant="danger"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([]);
                  }}
                >
                  <BsPersonCheck /> Created by me
                </Button>
                <Button variant="danger" onClick={toggleCreatedByMe}>
                  <BsX />
                </Button>
              </ButtonGroup>
            )}

            {searchFilter && (
              <ButtonGroup size="sm" className={styles.filter}>
                <Button variant="outline-info">
                  <BsSearch /> {searchFilter}
                </Button>
                <Button
                  variant="outline-info"
                  onClick={() => {
                    setSearchFilter("");
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            )}

            {!!(departureFrom || departureTo) && (
              <ButtonGroup size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.Departure]);
                  }}
                >
                  <BsCalendarDate />{" "}
                  {departureFrom && moment(departureFrom).format("YYYY-MM-DD")}{" "}
                  - {departureTo && moment(departureTo).format("YYYY-MM-DD")}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    setDepartureFrom(null);
                    setDepartureTo(null);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            )}

            {!!(arrivalFrom || arrivalTo) && (
              <ButtonGroup size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.Arrival]);
                  }}
                >
                  <BsCalendarCheck />{" "}
                  {arrivalFrom && moment(arrivalFrom).format("YYYY-MM-DD")} -{" "}
                  {arrivalTo && moment(arrivalTo).format("YYYY-MM-DD")}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    setArrivalFrom(null);
                    setArrivalTo(null);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            )}

            {/* {!!(startTime || endTime) && (
              <ButtonGroup size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.Time]);
                  }}
                >
                  <BsClock /> {startTime && moment(startTime).format("HH:mm")}
                  {" - "}
                  {endTime && moment(endTime).format("HH:mm")}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    setStartTime(null);
                    setEndTime(null);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            )} */}

            {selectedRoutes.map((route, index) => (
              <ButtonGroup key={index} size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.Route]);
                  }}
                >
                  <BsSignpost2 /> {route.description}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    toggleRoute(route);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            ))}

            {selectedStatuses.map((status, index) => (
              <ButtonGroup key={index} size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.Status]);
                  }}
                >
                  <BsCheckSquare /> {status.name}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    toggleStatus(status);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            ))}

            {selectedVehicleTypes.map((vehicleType, index) => (
              <ButtonGroup key={index} size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.VehicleType]);
                  }}
                >
                  <BsTruck /> {vehicleType.name}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    toggleVehicleType(vehicleType);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            ))}

            {selectedTrailerTypes.map((trailerType, index) => (
              <ButtonGroup key={index} size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.TrailerType]);
                  }}
                >
                  <FaTrailer /> {trailerType.name}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    toggleTrailerType(trailerType);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            ))}

            {selectedCargoTypes.map((cargoType, index) => (
              <ButtonGroup key={index} size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.CargoType]);
                  }}
                >
                  <BsBox /> {cargoType.name}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    toggleCargoType(cargoType);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            ))}

            {selectedCustomers.map((customer, index) => (
              <ButtonGroup key={index} size="sm" className={styles.filter}>
                <Button
                  variant="info"
                  onClick={() => {
                    setIsFiltersOpened(true);
                    setOpenedFilters([FilterCode.Customer]);
                  }}
                >
                  <BsPerson /> {customer.name}
                </Button>
                <Button
                  variant="info"
                  onClick={() => {
                    toggleCustomer(customer);
                  }}
                >
                  <BsX />
                </Button>
              </ButtonGroup>
            ))}
          </div>

          <BookingFilters
            show={isFiltersOpened}
            handleClose={() => {
              setIsFiltersOpened(false);
            }}
          />
        </div>
        <div>
          <DropdownButton id="table-actions" title="Table actions">
            <Dropdown.Item onClick={() => setIsColumnsOpened(true)}>
              <BsLayoutThreeColumns /> Edit columns
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() =>
                prepareTable((newWindow) => {
                  newWindow.print();
                })
              }
            >
              <BsPrinter /> Print table
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.ItemText
              className={styles.dropdownText}
              style={{ marginBottom: "0.5rem" }}
            >
              <span>
                <BsArrowsAngleContract /> Table size:
              </span>
              <ButtonGroup>
                {Object.values(TableSize).map((size, index) => (
                  <ToggleButton
                    key={index}
                    id={`table-size-radio-${size}`}
                    name="table-size"
                    type="radio"
                    variant="info"
                    value={size}
                    size="sm"
                    checked={tableSize === size}
                    onChange={(e) =>
                      setTableSize(e.currentTarget.value as TableSize)
                    }
                  >
                    {size === TableSize.S && "S"}
                    {size === TableSize.M && "M"}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Dropdown.ItemText>
            <Dropdown.ItemText className={styles.dropdownText}>
              <span>
                <BsSearch /> Per load:
              </span>
              <ButtonGroup>
                {Object.values(PerPage).map((size, index) => (
                  <ToggleButton
                    key={index}
                    id={`per-page-radio-${size}`}
                    name="per-page"
                    type="radio"
                    variant="info"
                    size="sm"
                    value={size}
                    checked={perPage === size}
                    onChange={(e) =>
                      setPerPage(e.currentTarget.value as PerPage)
                    }
                  >
                    {size}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Dropdown.ItemText>
          </DropdownButton>

          <BookingColumns
            show={isColumnsOpened}
            handleClose={() => setIsColumnsOpened(false)}
            table={table}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default BookingSettings;
